// om-tiles.ts — CloudFront-cache-first viewport reads for OM layers.
//
// Reads canonical, full-width chunk-row STRIPS (fixed height) via EXPLICIT byte
// ranges so the same ranges recur across pans/users → CloudFront edge-cache
// hits (verified MISS→HIT ~3×), and E-W panning is free (a strip spans all
// longitudes). A light in-memory session cache of decoded strips (+ per
// (model,refTime,validTime,variable) metadata) avoids re-fetch/re-decode within
// a session; CloudFront is the durable cache.
//
// The viewport's latitude band selects a strip range; the covered strips are
// assembled into one full-width row-band field, georeferenced as a standalone
// sub-grid (shifted projection origin / sub-bbox) so OmCustomLayer renders it
// unchanged. Assumes row0South = true (the OM convention the layer uses).

import proj4 from 'proj4'
import { resolveVariableMeta, rangeRead, type VariableMeta } from './om-fetch.js'
import { windowDecode2D, type ChunkMeta } from './om-decode.js'
import { buildProjection, buildRotatedProjection } from './om-projection.js'
import { makeRotatedTransform, type RotatedTransform } from './om-rotated.js'
import {
  octahedralGrid,
  octahedralResolutionFromCount,
  latBandRows,
  resampleGaussianToGrid,
} from './om-gaussian.js'

// Strip height in chunk-rows (chunks are 32 rows tall → 8 ⇒ 256-row strips).
// Tunable: larger = fewer/larger range requests (fewer cache entries, more
// per-request bytes); smaller = finer cache reuse + less N/S over-read.
export const STRIP_CHUNK_ROWS = 8

export interface OmProjectionDef {
  proj4def: string
  x0: number
  y0: number
  dx: number
  dy: number
  // Rotated lat/lon grids (e.g. GEM HRDPS): x0/y0/dx/dy are rotated-lon/lat
  // DEGREES and per-vertex mapping goes through the sphere rotation (om-rotated).
  rotatedPole?: { lonP: number; latP: number; cRotLon: number }
}

// Everything the layer service needs to build an OmField for the visible band.
export interface ViewportField {
  values: Int32Array // raw (pre-scale) ints, row-major over [nLat × nLon]
  scale: number
  nLon: number
  nLat: number // band height (rows)
  bbox: [number, number, number, number] // [latMin, lonMin, latMax, lonMax] of the band
  row0South: boolean
  projection?: OmProjectionDef // sub-grid projection (origin shifted to the band)
  rowStart: number // global array-row of band row 0
  fileSize: number
}

// ---------------------------------------------------------------------------
// Caches (in-memory, per session). CloudFront is the durable cache.
// ---------------------------------------------------------------------------

const metaCache = new Map<string, Promise<VariableMeta>>()

interface DecodedStrip {
  values: Int32Array // rows*nLon, row-major; row 0 == array row `rowStart`
  rowStart: number
  rows: number
}
const stripCache = new Map<string, DecodedStrip>()
const STRIP_CACHE_MAX = 600 // LRU by insertion order (~tens of MB of Int32 strips)

function lruGet(key: string): DecodedStrip | undefined {
  const v = stripCache.get(key)
  if (v) {
    stripCache.delete(key)
    stripCache.set(key, v) // bump to MRU
  }
  return v
}
function lruSet(key: string, v: DecodedStrip): void {
  stripCache.set(key, v)
  if (stripCache.size > STRIP_CACHE_MAX) {
    const oldest = stripCache.keys().next().value
    if (oldest !== undefined) stripCache.delete(oldest)
  }
}

/** Per (url, variable) decode metadata, memoized for the session. */
export function getMeta(
  url: string,
  varName: string,
  fetchImpl?: typeof fetch
): Promise<VariableMeta> {
  const key = `${url}|${varName}`
  let p = metaCache.get(key)
  if (!p) {
    p = resolveVariableMeta(url, varName, fetchImpl ? { fetchImpl } : {})
    metaCache.set(key, p)
    p.catch(() => metaCache.delete(key)) // allow retry after a failed fetch
  }
  return p
}

/** Test/utility: clear session caches. */
export function clearOmCaches(): void {
  metaCache.clear()
  stripCache.clear()
}

// ---------------------------------------------------------------------------
// Strip geometry + loading.
// ---------------------------------------------------------------------------

function numStrips(meta: VariableMeta): number {
  return Math.ceil(meta.grid[0] / STRIP_CHUNK_ROWS)
}

// chunk-row range [cr0, cr1] (inclusive) + chunk-index span [ci0, ciEnd) of strip s.
function stripChunkSpan(
  meta: VariableMeta,
  s: number
): { cr0: number; cr1: number; ci0: number; ciEnd: number } {
  const gridRows = meta.grid[0]
  const gridCols = meta.grid[1]
  const cr0 = s * STRIP_CHUNK_ROWS
  const cr1 = Math.min(cr0 + STRIP_CHUNK_ROWS, gridRows) - 1
  return { cr0, cr1, ci0: cr0 * gridCols, ciEnd: (cr1 + 1) * gridCols }
}

async function loadStrip(
  url: string,
  varName: string,
  meta: VariableMeta,
  s: number,
  fetchImpl?: typeof fetch
): Promise<DecodedStrip> {
  const key = `${url}|${varName}|${s}`
  const hit = lruGet(key)
  if (hit) return hit

  const { cr0, cr1, ci0, ciEnd } = stripChunkSpan(meta, s)
  const spanStart = meta.starts[ci0]
  const spanEnd = meta.starts[ciEnd]
  const bytes = await rangeRead(url, spanStart, spanEnd, fetchImpl ?? fetch.bind(globalThis))
  const cm: ChunkMeta = { dims: meta.dims, chunk: meta.chunk, grid: meta.grid, starts: meta.starts }
  const win = windowDecode2D(cm, bytes, cr0, cr1, 0, meta.grid[1] - 1, spanStart)
  const strip: DecodedStrip = { values: win.values, rowStart: win.rowStart, rows: win.winRows }
  lruSet(key, strip)
  return strip
}

// Assemble strips [sFrom..sTo] into one full-width row-band (Int32, rows*nLon).
async function readStripBand(
  url: string,
  varName: string,
  meta: VariableMeta,
  sFrom: number,
  sTo: number,
  fetchImpl?: typeof fetch
): Promise<{ values: Int32Array; rowStart: number; rows: number }> {
  const nLon = meta.dims[1]
  const strips = await Promise.all(
    Array.from({ length: sTo - sFrom + 1 }, (_, i) =>
      loadStrip(url, varName, meta, sFrom + i, fetchImpl)
    )
  )
  const rowStart = strips[0].rowStart
  const last = strips[strips.length - 1]
  const rows = last.rowStart + last.rows - rowStart
  const values = new Int32Array(rows * nLon)
  for (const st of strips) values.set(st.values, (st.rowStart - rowStart) * nLon)
  return { values, rowStart, rows }
}

// ---------------------------------------------------------------------------
// Viewport → array-row band → strip range.
// ---------------------------------------------------------------------------

// Map a (lat, lon) to a fractional array row (row0South=true convention).
function arrayRowAt(
  lat: number,
  lon: number,
  gridBbox: [number, number, number, number],
  nLat: number,
  toProj: proj4.Converter | null,
  proj: OmProjectionDef | null,
  rot: RotatedTransform | null
): number {
  if (rot && proj) {
    const ry = rot.toRot(lon, lat)[1] // true → rotated latitude
    return (ry - proj.y0) / proj.dy // row0South ⇒ array row increases with rotated lat
  }
  if (toProj && proj) {
    const [, y] = toProj.inverse([lon, lat]) // WGS84 → projected (x, y)
    return (y - proj.y0) / proj.dy // row0South ⇒ array row increases with projected y
  }
  const [latMin, , latMax] = gridBbox
  return ((lat - latMin) / (latMax - latMin)) * (nLat - 1)
}

// Inclusive strip range covering the viewport's latitude band, or null if the
// grid isn't visible. Strips are full-width, so only the row (lat) extent matters.
export function stripRangeForViewport(
  viewport: [number, number, number, number], // [latMin, lonMin, latMax, lonMax]
  gridBbox: [number, number, number, number],
  meta: VariableMeta,
  proj: OmProjectionDef | null
): { sFrom: number; sTo: number } | null {
  const nLat = meta.dims[0]
  const rot = proj?.rotatedPole ? makeRotatedTransform(proj.rotatedPole) : null
  const toProj = proj && !rot ? proj4(proj.proj4def, 'WGS84') : null
  const [vLatMin, vLonMin, vLatMax, vLonMax] = viewport
  // Sample a 4×4 grid across the viewport; the LCC fan curves, so corners alone
  // can miss the extreme rows — sampling the interior is robust.
  let rMin = Infinity
  let rMax = -Infinity
  for (let i = 0; i < 4; i++) {
    const lat = vLatMin + (i / 3) * (vLatMax - vLatMin)
    for (let j = 0; j < 4; j++) {
      const lon = vLonMin + (j / 3) * (vLonMax - vLonMin)
      const r = arrayRowAt(lat, lon, gridBbox, nLat, toProj, proj, rot)
      if (r < rMin) rMin = r
      if (r > rMax) rMax = r
    }
  }
  if (!Number.isFinite(rMin) || !Number.isFinite(rMax)) return null
  // No overlap with the grid at all.
  if (rMax < 0 || rMin > nLat - 1) return null
  const r0 = Math.max(0, Math.floor(rMin) - 1)
  const r1 = Math.min(nLat - 1, Math.ceil(rMax) + 1)
  const stripH = STRIP_CHUNK_ROWS * meta.chunk[0]
  const sFrom = Math.max(0, Math.floor(r0 / stripH))
  const sTo = Math.min(numStrips(meta) - 1, Math.floor(r1 / stripH))
  if (sTo < sFrom) return null
  return { sFrom, sTo }
}

// ---------------------------------------------------------------------------
// Top-level: read the visible band as a render-ready field.
// ---------------------------------------------------------------------------

export interface ReadViewportOptions {
  viewport: [number, number, number, number] // [latMin, lonMin, latMax, lonMax]
  gridBbox: [number, number, number, number]
  crsWkt: string | null
  fetchImpl?: typeof fetch
}

// Returns the visible latitude band as a standalone sub-grid (shifted projection
// origin / sub-bbox), or null if the grid isn't in view. Caller maps Int32 →
// Float32 (scale + fill sentinel) and constructs the OmField.
export async function readViewportField(
  url: string,
  varName: string,
  opts: ReadViewportOptions
): Promise<ViewportField | null> {
  // Reduced Gaussian grids (ECMWF IFS 9km, dims [1, ~6.6M]) are NOT rectangular
  // and can't render on the regular-grid path (a 1-row "grid" smears vertically
  // and overflows the value texture). Deferred to Phase B (native Gaussian).
  if (opts.crsWkt?.includes('Reduced Gaussian')) return null

  const meta = await getMeta(url, varName, opts.fetchImpl)
  const nLon = meta.dims[1]
  const nLat = meta.dims[0]
  if (meta.dims.length !== 2 || nLat < 2 || nLon < 2) return null

  let proj: OmProjectionDef | null = null
  if (opts.crsWkt?.startsWith('PROJCRS')) {
    const built = buildProjection(opts.crsWkt, opts.gridBbox, nLon, nLat)
    if (built) proj = built.projection
  } else if (opts.crsWkt?.includes('ob_tran') || opts.crsWkt?.includes('Rotated Lat/Lon')) {
    // Rotated lat/lon grid (e.g. GEM HRDPS): regular in rotated coords.
    const built = buildRotatedProjection(opts.crsWkt, opts.gridBbox, nLon, nLat)
    if (built) proj = built.projection
  }

  const range = stripRangeForViewport(opts.viewport, opts.gridBbox, meta, proj)
  if (!range) return null

  const band = await readStripBand(url, varName, meta, range.sFrom, range.sTo, opts.fetchImpl)

  // Georeference the band as a standalone sub-grid (row0South = true).
  const [gLatMin, gLonMin, gLatMax, gLonMax] = opts.gridBbox
  let projection: OmProjectionDef | undefined
  let bbox: [number, number, number, number]
  if (proj) {
    // Shift the projected origin to the band's first array row: y0' = y0 + rowStart*dy.
    // (For rotated grids dy is in rotated-lat degrees; rotatedPole carried through.)
    projection = {
      ...proj,
      y0: proj.y0 + band.rowStart * proj.dy,
    }
    // bbox is metadata only for the projected branch; give a sane lat span.
    const latOf = (r: number): number => gLatMin + (r / (nLat - 1)) * (gLatMax - gLatMin)
    bbox = [latOf(band.rowStart), gLonMin, latOf(band.rowStart + band.rows - 1), gLonMax]
  } else {
    const latOf = (r: number): number => gLatMin + (r / (nLat - 1)) * (gLatMax - gLatMin)
    bbox = [latOf(band.rowStart), gLonMin, latOf(band.rowStart + band.rows - 1), gLonMax]
  }

  return {
    values: band.values,
    scale: meta.scale,
    nLon,
    nLat: band.rows,
    bbox,
    row0South: true,
    ...(projection ? { projection } : {}),
    rowStart: band.rowStart,
    fileSize: meta.fileSize,
  }
}

// ---------------------------------------------------------------------------
// Reduced-Gaussian read (ECMWF IFS 9km, O1280). The file is a flat 1-D point
// stream; window it by the viewport's latitude band (a CONTIGUOUS point range →
// one coalesced range read), decode, then resample the window onto a regular
// viewport sub-grid the existing GPU path renders. Returns null if the file
// isn't an octahedral Gaussian grid or the viewport is off-grid.
// ---------------------------------------------------------------------------

// ~9km in degrees — target resampled cell size (capped to keep the texture small).
const GAUSSIAN_CELL_DEG = 0.07
const GAUSSIAN_MAX_OUT = 2048

export async function readViewportFieldGaussian(
  url: string,
  varName: string,
  opts: ReadViewportOptions
): Promise<ViewportField | null> {
  const meta = await getMeta(url, varName, opts.fetchImpl)
  const total = meta.dims[meta.dims.length - 1]
  const n = octahedralResolutionFromCount(total)
  if (n == null) return null
  const grid = octahedralGrid(n)

  const [vLatMin, vLonMin, vLatMax, vLonMax] = opts.viewport
  const latMin = Math.max(vLatMin, grid.lats[grid.nLat - 1])
  const latMax = Math.min(vLatMax, grid.lats[0])
  if (latMin >= latMax) return null

  // Viewport lat band → Gaussian rows → contiguous point range → chunk range.
  let [r0, r1] = latBandRows(grid, latMin, latMax)
  r0 = Math.max(0, r0 - 2)
  r1 = Math.min(grid.nLat - 1, r1 + 2)
  const cw = meta.chunk[meta.chunk.length - 1]
  const cc0 = Math.floor(grid.offsets[r0] / cw)
  const cc1 = Math.floor((grid.offsets[r1 + 1] - 1) / cw)
  const spanStart = meta.starts[cc0]
  const spanEnd = meta.starts[cc1 + 1]
  const bytes = await rangeRead(url, spanStart, spanEnd, opts.fetchImpl ?? fetch.bind(globalThis))
  const cm: ChunkMeta = { dims: meta.dims, chunk: meta.chunk, grid: meta.grid, starts: meta.starts }
  const win = windowDecode2D(cm, bytes, 0, 0, cc0, cc1, spanStart)
  const pointOffset = win.colStart

  // Resample the window onto a regular sub-grid over the visible viewport.
  const bbox: [number, number, number, number] = [
    latMin,
    Math.max(vLonMin, -180),
    latMax,
    Math.min(vLonMax, 180),
  ]
  const outNLat = Math.min(
    GAUSSIAN_MAX_OUT,
    Math.max(2, Math.round((bbox[2] - bbox[0]) / GAUSSIAN_CELL_DEG) + 1)
  )
  const outNLon = Math.min(
    GAUSSIAN_MAX_OUT,
    Math.max(2, Math.round((bbox[3] - bbox[1]) / GAUSSIAN_CELL_DEG) + 1)
  )
  const values = resampleGaussianToGrid(grid, win.values, bbox, outNLon, outNLat, pointOffset)

  return {
    values,
    scale: meta.scale,
    nLon: outNLon,
    nLat: outNLat,
    bbox,
    row0South: true,
    rowStart: 0,
    fileSize: meta.fileSize,
  }
}
